import ReportToolbarButton from '../shared/ReportToolbarButton';
import { Typography, LinearProgress, Tab, Box, Tooltip, useTheme, useMediaQuery, IconButton, Skeleton, Stack, Divider, Card, CardContent, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, ThemeProvider } from '@mui/material';
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import TableCard from '../../system/table-card';
import ResponsiveTabs from '../../system/responsive-tabs';
import { generatePDF } from '../../dwi-functions';
import _ from 'lodash';
import Comments from '../../views/comments';
import { AppVarsContext } from '../../context/AppVarsContext';
import { useToken } from '../../Login/TokenProvider';
import { MdPhoneIphone, MdOutlinePhoneAndroid, MdTabletAndroid, MdFilterAlt } from "react-icons/md";
import { FcAndroidOs } from "react-icons/fc";
import { IoIosTabletLandscape } from "react-icons/io";
import { IoDesktop, IoLogoApple } from "react-icons/io5";
import CreativeDetailTabs from './CreativeDetailsTab';
import LoadingDataBox from '../../system/loading-data-box';
import TrendChart from '../../report-components/trend-chart';
import ReactApexChart from 'react-apexcharts';
import ReportConversionSelect from '../../system/controls/report-conversions';
import { applyTrendTooltip } from '../../system/apex-tooltip';
import { useUserProfile } from '../../context/UserProvider';
import ReportPerformanceOverview, { ReportSectionIntro } from '../shared/ReportPerformanceOverview';
import { analyticsColors, createReportSurfaceTheme, getReportSurfaceSx, getReportPageSx, getAnalyticsReportTabsSx, reportColors, styleReportChart } from '../shared/report-design';
import MetaEngagementSection, { getMetaEngagementColumns } from '../shared/metaEngagementSection';
import ReportQuestionAssistant from '../../system/controls/report-question-assistant';
import CampaignSelectionFilter, { normalizeCampaignOptions, selectionPayload } from '../shared/campaignSelectionFilter';
import { normalizeReportProfileVisibility, resolveShowCost } from '../../system/report-visibility';
import { formatUsd } from '../../system/report-formatters';
import LocationMap from '../../system/geo/LocationMap';

const META_ASSISTANT_VIEWS = [
    'Performance Overview',
    'Campaign',
    'Audiences',
    'Engagement',
    'Demographics',
    'Device Info',
    'Locations',
    'Notes'
];


function TabPanel(props) {
    const { children, value, index, ...other } = props;
    if (value !== index) return null;
    return (
        <Box role="tabpanel" id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} sx={{ minWidth: 0 }} {...other}>
            {children}
        </Box>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const normalizeDeviceKey = (value) => String(value || '').toLowerCase();
const deviceLabelMap = {
    iphone: 'iPhone',
    android_smartphone: 'Android Phone',
    android_tablet: 'Android Tablet',
    ipad: 'iPad',
    desktop: 'Desktop',
    unknown: 'Unknown'
};

const ageSortValue = (label) => {
    const value = String(label || '').trim().toLowerCase();
    if (!value) return 998;
    if (value.includes('unknown') || value.includes('other')) return 999;
    const plusMatch = value.match(/^(\d+)\+/);
    if (plusMatch) return Number(plusMatch[1]);
    const rangeMatch = value.match(/^(\d+)\s*-\s*(\d+)/);
    if (rangeMatch) return Number(rangeMatch[1]);
    const numMatch = value.match(/\d+/);
    if (numMatch) return Number(numMatch[0]);
    return 998;
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatCurrency = (value) => formatUsd(value);
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const getConversionSelectionLabel = (value, fallback = 'Selected conversions') => {
    if (!Array.isArray(value) || !value.length) return fallback;
    const labels = value
        .map((item) => item?.ACTION || item?.name || item?.id || item)
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    return labels.length ? labels.join(', ') : fallback;
};
const truncateText = (value, max = 28) => {
    const text = String(value || '');
    if (text.length <= max) return text;
    return `${text.slice(0, max - 3)}...`;
};

const getNumericValue = (row, keys) => {
    for (const key of keys) {
        const value = row?.[key];
        if (value === undefined || value === null || value === '') continue;
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
    }
    return 0;
};

const hasRowValue = (row, keys) => (
    keys.some((key) => row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== '')
);

const resolveRowLabel = (row, keys) => {
    for (const key of keys) {
        const value = String(row?.[key] || '').trim();
        if (value) return value;
    }
    return 'Unknown';
};

const getMetaEngagementMetrics = (row) => {
    const reactionKeys = ['REACTIONS', 'reactions', 'POST_REACTIONS', 'post_reactions', 'LIKES', 'likes'];
    const commentKeys = ['COMMENTS', 'comments', 'POST_COMMENTS', 'post_comments'];
    const shareKeys = ['SHARES', 'shares', 'POST_SHARES', 'post_shares'];
    const saveKeys = ['SAVES', 'saves', 'POST_SAVES', 'post_saves'];
    const totalKeys = ['ENGAGEMENT', 'engagement', 'TOTAL_ENGAGEMENT', 'total_engagement', 'POST_ENGAGEMENT', 'post_engagement'];

    const reactions = getNumericValue(row, reactionKeys);
    const comments = getNumericValue(row, commentKeys);
    const shares = getNumericValue(row, shareKeys);
    const saves = getNumericValue(row, saveKeys);
    const explicitTotal = getNumericValue(row, totalKeys);
    const summedTotal = reactions + comments + shares + saves;

    return {
        reactions,
        comments,
        shares,
        saves,
        engagementTotal: explicitTotal > 0 ? explicitTotal : summedTotal,
        hasEngagementSignal: hasRowValue(row, [...reactionKeys, ...commentKeys, ...shareKeys, ...saveKeys, ...totalKeys])
    };
};

const buildMetaSummaryRow = (row, labelKeys) => {
    const impressions = getNumericValue(row, ['IMPRESSIONS', 'impressions']);
    const clicks = getNumericValue(row, ['CLICKS', 'clicks']);
    const conversions = getNumericValue(row, ['CONVERSIONS', 'conversions']);
    const cost = getNumericValue(row, ['COST', 'cost']);
    const revenue = getNumericValue(row, ['REVENUE', 'revenue']);
    const ctr = getNumericValue(row, ['CTR', 'ctr']) || (impressions > 0 ? (clicks / impressions) * 100 : 0);
    const convRate =
        getNumericValue(row, ['CONV_RATE', 'CONVRATE', 'convrate', 'convRate']) ||
        (clicks > 0 ? (conversions / clicks) * 100 : 0);
    const roas = cost > 0 ? revenue / cost : 0;
    const engagement = getMetaEngagementMetrics(row);

    return {
        label: resolveRowLabel(row, labelKeys),
        impressions,
        clicks,
        conversions,
        cost,
        revenue,
        ctr,
        convRate,
        roas,
        ...engagement,
        engagementRate: impressions > 0 ? (engagement.engagementTotal / impressions) * 100 : 0,
        raw: row
    };
};

const normalizePayloadRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

const buildMetaEngagementTotals = (totalsPayload, rows) => {
    const fallbackTotals = rows.reduce((acc, row) => {
        acc.impressions += row.impressions || 0;
        acc.clicks += row.clicks || 0;
        acc.reactions += row.reactions || 0;
        acc.comments += row.comments || 0;
        acc.shares += row.shares || 0;
        acc.saves += row.saves || 0;
        acc.engagementTotal += row.engagementTotal || 0;
        return acc;
    }, {
        impressions: 0,
        clicks: 0,
        reactions: 0,
        comments: 0,
        shares: 0,
        saves: 0,
        engagementTotal: 0
    });

    if (!totalsPayload || typeof totalsPayload !== 'object') {
        return fallbackTotals;
    }

    const metrics = getMetaEngagementMetrics(totalsPayload);

    return {
        impressions: hasRowValue(totalsPayload, ['IMPRESSIONS', 'impressions'])
            ? getNumericValue(totalsPayload, ['IMPRESSIONS', 'impressions'])
            : fallbackTotals.impressions,
        clicks: hasRowValue(totalsPayload, ['CLICKS', 'clicks'])
            ? getNumericValue(totalsPayload, ['CLICKS', 'clicks'])
            : fallbackTotals.clicks,
        reactions: hasRowValue(totalsPayload, ['REACTIONS', 'reactions', 'POST_REACTIONS', 'post_reactions', 'LIKES', 'likes'])
            ? metrics.reactions
            : fallbackTotals.reactions,
        comments: hasRowValue(totalsPayload, ['COMMENTS', 'comments', 'POST_COMMENTS', 'post_comments'])
            ? metrics.comments
            : fallbackTotals.comments,
        shares: hasRowValue(totalsPayload, ['SHARES', 'shares', 'POST_SHARES', 'post_shares'])
            ? metrics.shares
            : fallbackTotals.shares,
        saves: hasRowValue(totalsPayload, ['SAVES', 'saves', 'POST_SAVES', 'post_saves'])
            ? metrics.saves
            : fallbackTotals.saves,
        engagementTotal: hasRowValue(totalsPayload, ['ENGAGEMENT', 'engagement', 'TOTAL_ENGAGEMENT', 'total_engagement', 'POST_ENGAGEMENT', 'post_engagement'])
            ? metrics.engagementTotal
            : fallbackTotals.engagementTotal
    };
};

const normalizeMetaEngagementPayload = (payload) => {
    const rows = normalizePayloadRows(payload).map((row) => (
        buildMetaSummaryRow(row, ['ADSET', 'AD_SET', 'ADSET_NAME', 'AD_SET_NAME', 'NAME', 'CAMPAIGN', 'CAMPAIGN_NAME'])
    ));
    const totalsPayload = Array.isArray(payload) ? null : (payload?.totals || payload?.summary || payload?.total || null);

    return {
        rows,
        totals: buildMetaEngagementTotals(totalsPayload, rows)
    };
};

const ensureApexOptions = (options, chartType, theme) => {
    const base = options && typeof options === 'object' ? options : {};
    const merged = styleReportChart(theme, {
        ...base,
        chart: { ...base.chart, type: chartType },
        colors: base.colors || reportColors.categories,
    });
    return applyTrendTooltip(theme, merged);
};

const parseSettings = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch (error) {
            return null;
        }
    }
    return null;
};

const hasConversionSpecValue = (value) => {
    if (value == null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number') return Number.isFinite(value);
    return false;
};

const pickMetaConversionSpec = (settings) => {
    if (!settings || typeof settings !== 'object') return null;
    const candidates = [
        settings?.conversion_defaults?.meta,
        settings?.conversionActions?.meta,
        settings?.conversion_actions?.meta,
        settings?.conversionSpec?.meta,
        settings?.conversion_spec?.meta,
        settings?.conversions?.meta,
        settings?.conversionActions,
        settings?.conversion_actions,
        settings?.conversionSpec,
        settings?.conversion_spec,
        settings?.conversions,
        settings?.ad_sets?.meta?.conversionActions,
        settings?.ad_sets?.meta?.conversion_actions,
        settings?.ad_sets?.meta?.conversionSpec,
        settings?.ad_sets?.meta?.conversion_spec,
        settings?.ad_sets?.meta?.conversions
    ];
    return candidates.find(hasConversionSpecValue) || null;
};



const Meta = (props) => {
    const { user } = useUserProfile();
    const pDate = new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('sDate'))) + "-" + new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('eDate')));
    const [value, setValue] = useState(0);
    const [cid] = useState(sessionStorage.getItem('rid'));
    const [loading, setLoading] = useState(true);
    const [reportProfile, setreportProfile] = useState();
    const [agePie, setAgePie] = useState();
    const [ageData, setAgeData] = useState();
    const [genderData, setGenderData] = useState();
    const [data, setMetaData] = useState();
    const [campaignData, setCampaignData] = useState([]);
    const [locationData, setLocationData] = useState([]);
    const [devicesData, setDevicesData] = useState();
    const [adsetData, setAdsetData] = useState();
    const [engagementData, setEngagementData] = useState({ rows: [], totals: null });
    const token = useToken();
    const admintoken = parseInt(token.token.token);
    const [imageUrls, setImageUrls] = useState({});
    const { appVars, appLoading } = useContext(AppVarsContext);
    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const surfaceCardSx = getReportSurfaceSx(theme);
    const plainCardSx = surfaceCardSx;
    const tabsShellSx = { ...surfaceCardSx, p: 1 };
    const tabsSx = getAnalyticsReportTabsSx();
    const filterButtonSx = { minHeight: 44, textTransform: 'none' };
    const showCost = resolveShowCost(reportProfile);
    const pageShellSx = { ...getReportPageSx(theme), '& .meta-executive-content .MuiPaper-root': surfaceCardSx };
    const firstCol = 275;
    const reportRef1 = useRef()
    const reportRef2 = useRef()
    const reportRef3 = useRef()
    const reportRef4 = useRef()
    const reportRef5 = useRef()
    const appReadyRef = useRef(false);
    const reportProfileRef = useRef(null);
    const fetchReportDataRef = useRef(null);
    const [conversionActions, setConversionActions] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const fetchIdRef = useRef(0);
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [selectedCampaignIds, setSelectedCampaignIds] = useState(null);
    const [draftCampaignIds, setDraftCampaignIds] = useState([]);
    const metaAssistantConfig = useMemo(() => ({
        activeView: META_ASSISTANT_VIEWS[value] || 'Performance Overview',
        filters: {
            conversions: Array.isArray(conversionActions)
                ? conversionActions.map((item) => item?.ACTION || item?.name || item?.id || item)
                : [],
            campaignIds: selectedCampaignIds,
        }
    }), [conversionActions, selectedCampaignIds, value]);
    const normalizeConversionSpec = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value
                .map((entry) => {
                    if (entry == null) return null;
                    if (typeof entry === 'string' || typeof entry === 'number') return String(entry);
                    if (typeof entry === 'object') {
                        return entry.name || entry.label || entry.id || entry.value || entry.resourceName || entry.action_type || entry.conversion || null;
                    }
                    return null;
                })
                .filter(Boolean);
        }
        if (typeof value === 'object') {
            const nested =
                value?.conversions ||
                value?.conversion ||
                value?.conversion_actions ||
                value?.conversionActions ||
                value?.values ||
                [];
            return normalizeConversionSpec(nested);
        }
        return [];
    };

    const userSettings = useMemo(() => parseSettings(user?.settings), [user?.settings]);
    const reportSettings = useMemo(() => parseSettings(reportProfile?.settings), [reportProfile?.settings]);

    const defaultConversionSpec = useMemo(() => {
        const spec =
            pickMetaConversionSpec(userSettings) ||
            pickMetaConversionSpec(user) ||
            pickMetaConversionSpec(reportSettings) ||
            null;
        return normalizeConversionSpec(spec);
    }, [userSettings, user, reportSettings]);

    const conversionSessionKey = 'conversionSpec_meta';
    const conversionSessionRef = useRef({ has: false, value: [] });
    const loadSessionConversions = () => {
        const raw = sessionStorage.getItem(conversionSessionKey);
        if (raw === null) {
            conversionSessionRef.current = { has: false, value: [] };
            return conversionSessionRef.current;
        }
        try {
            const parsed = JSON.parse(raw);
            const normalized = normalizeConversionSpec(parsed);
            conversionSessionRef.current = { has: true, value: normalized };
            return conversionSessionRef.current;
        } catch (error) {
            conversionSessionRef.current = { has: true, value: [] };
            return conversionSessionRef.current;
        }
    };

    useEffect(() => {
        if (!reportProfile) return;
        const sessionConversions = loadSessionConversions();
        if (sessionConversions.has) {
            setConversionActions(sessionConversions.value);
            return;
        }
        if (defaultConversionSpec.length > 0) {
            setConversionActions(defaultConversionSpec);
        }
    }, [reportProfile, defaultConversionSpec]);


    //console.log('meta appVars',JSON.parse(appVars.tooltips).impression);
    const [client, setClient] = useState({
        clientId: sessionStorage.getItem('apiID'),
        apiPort: sessionStorage.getItem('aPort')
    });
    const fetchImageUrl = async (imageName) => {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify({ "image": imageName }),
            redirect: "follow"
        };
        try {
            const response = await fetch(`${appVars.api_url}/meta/creatives/image`, requestOptions);
            const data = await response.json();
            return data.IMAGE_ASSET_URL;
        } catch (error) {
            console.error(error);
            return 'https://api.digitalwerks.com/imageFiles/no-image-icon.png'; // Default image on error
        }
    };








    const handleChange = (event, newValue) => {
        setValue(newValue);
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    };


    async function fetchReportData(nextConversions) {
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        props.setLoading(true);
        const sessionConversions = loadSessionConversions();
        let conversions = Array.isArray(nextConversions)
            ? nextConversions
            : (conversionActions.length ? conversionActions : sessionConversions.value);
        conversions = normalizeConversionSpec(conversions);
        if (!conversions.length && defaultConversionSpec.length && !sessionConversions.has) {
            conversions = defaultConversionSpec;
        }

        const body = JSON.stringify({
            cid: sessionStorage.getItem('rid'),
            sdate: sessionStorage.getItem('sDate'),
            edate: sessionStorage.getItem('eDate'),
            granularity: 'region',
            conversions,
            ...selectionPayload(selectedCampaignIds)
        });
        const engagementBody = JSON.stringify({
            cid: sessionStorage.getItem('rid'),
            sdate: sessionStorage.getItem('sDate'),
            edate: sessionStorage.getItem('eDate'),
            ...selectionPayload(selectedCampaignIds)
        });

        const requestOptions = {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body,
            redirect: 'follow'
        };
        const engagementRequestOptions = {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: engagementBody,
            redirect: 'follow'
        };
        const campaignOptionRequestOptions = {
            ...requestOptions,
            body: JSON.stringify({
                cid: sessionStorage.getItem('rid'),
                sdate: sessionStorage.getItem('sDate'),
                edate: sessionStorage.getItem('eDate'),
                campaignFilterActive: false,
                campaignIds: [],
            }),
        };

        try {
            const [
                metaDataRes,
                devicesDataRes,
                campaignRes,
                locationRes,
                adsetsRes,
                creativesRes,
                ageDataRes,
                engagementRes,
                campaignOptionsRes
            ] = await Promise.all([
                fetch(`${appVars.api_url}/meta/data`, requestOptions),
                fetch(`${appVars.api_url}/meta/devices`, requestOptions),
                fetch(`${appVars.api_url}/meta/campaigns`, requestOptions),
                fetch(`${appVars.api_url}/meta/locations`, requestOptions),
                fetch(`${appVars.api_url}/meta/adsets`, requestOptions),
                fetch(`${appVars.api_url}/meta/creatives`, requestOptions),
                fetch(`${appVars.api_url}/meta/data/age`, requestOptions),
                fetch(`${appVars.api_url}/meta/engagement`, engagementRequestOptions).then(async (response) => ({
                    ok: response.ok,
                    data: await response.json().catch(() => ({}))
                })),
                fetch(`${appVars.api_url}/meta/campaign-options`, campaignOptionRequestOptions)
            ]);

            const [
                metaData,
                devicesData,
                campaignData,
                locationPayload,
                adsetData,
                creativeData,
                ageData,
                campaignOptionsJson
            ] = await Promise.all([
                metaDataRes.json(),
                devicesDataRes.json(),
                campaignRes.json(),
                locationRes.json(),
                adsetsRes.json(),
                creativesRes.json(),
                ageDataRes.json(),
                campaignOptionsRes.json()
            ]);

            if (fetchId !== fetchIdRef.current) return;

            setMetaData(metaData);
            setDevicesData(devicesData);
            setCampaignData(campaignData);
            setCampaignOptions(normalizeCampaignOptions(campaignOptionsJson?.campaigns));
            setLocationData(Array.isArray(locationPayload) ? locationPayload : (Array.isArray(locationPayload?.rows) ? locationPayload.rows : []));
            setAdsetData(adsetData);
            setEngagementData(engagementRes.ok ? normalizeMetaEngagementPayload(engagementRes.data) : { rows: [], totals: null });

            console.log('meta data', creativeData);

            // preload creative image URLs
            if (creativeData[1]?.[0]) {
                await Promise.all(creativeData[1][0].map(async (item) => {
                    const imageUrl = await fetchImageUrl(item.NAME);
                    setImageUrls(prev => ({ ...prev, [item.NAME]: imageUrl }));
                }));
            }

            // Transform age data
            const genderBreakdown = _(ageData)
                .groupBy('GENDER')
                .map((group, gender) => ({
                    name: gender,
                    count: _.sumBy(group, 'CLICKS')
                }))
                .value();

            const agePieData = _(ageData)
                .groupBy('AGE')
                .map((group, age) => ({
                    name: age,
                    count: _.sumBy(group, 'CLICKS')
                }))
                .value();

            const ageBarData = _(ageData)
                .groupBy('AGE')
                .map((group, age) => ({
                    ageRange: age,
                    female: _.sumBy(group, o => o.GENDER === 'female' ? +o.CLICKS : 0),
                    male: _.sumBy(group, o => o.GENDER === 'male' ? +o.CLICKS : 0),
                    unknown: _.sumBy(group, o => o.GENDER === 'unknown' ? +o.CLICKS : 0)
                }))
                .value();

            setGenderData(genderBreakdown);
            setAgePie(agePieData);
            setAgeData(ageBarData);
            props.setLoading(false);
            setLoading(false);
            //console.log('meta data', data);
        } catch (err) {
            if (fetchId === fetchIdRef.current) console.error("Error fetching Meta report data:", err);
        } finally {
            if (fetchId === fetchIdRef.current) {
                props.setLoading(false);
                setLoading(false);
            }
        }
    }

    async function fetchProfile() {
        try {
            const rp = await fetch(`${appVars.api_url}/reportprofile/${sessionStorage.getItem('rid')}`, {
                method: "GET",
            });
            const response = normalizeReportProfileVisibility(await rp.json());
            setreportProfile(response);
        } catch (error) {
            console.error('Error fetching report profile:', error);
        }
    }



    useEffect(() => {
        appReadyRef.current = !appLoading;
    }, [appLoading]);

    useEffect(() => {
        // console.log('Meta reportProfile changed:', reportProfile);
        reportProfileRef.current = reportProfile;
    }, [reportProfile]);

    useEffect(() => {
        fetchReportDataRef.current = fetchReportData;
    });

    useEffect(() => {
        const handleStorageChange = (e) => {
            console.log('Meta dc event detail:', e?.detail);
            if (!appReadyRef.current) return;
            if (!reportProfileRef.current) return;
            // Defer so sessionStorage updates settle before reading sDate/eDate.
            setTimeout(() => {
                try {
                    fetchReportDataRef.current?.();
                } catch (err) {
                    console.error('Meta dc refetch error:', err);
                }
            }, 0);
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, []);




    /**/
    useEffect(() => {
        if (!appLoading) { fetchProfile(); }


    }, [appLoading]);

    useEffect(() => {
        if (appLoading) return;
        if (!reportProfile) return;
        fetchReportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appLoading, reportProfile, selectedCampaignIds]);


    const buildTrendFromStats = (stats) => {

        if (!stats?.impressions?.series) return [];
        const keys = ['impressions', 'clicks', 'conversions', 'cost', 'revenue'];
        const map = new Map();
        stats.impressions.series.forEach((point) => {
            map.set(point.x, { date: point.x, impressions: point.y });
        });
        keys.forEach((key) => {
            if (!stats[key]?.series) return;
            stats[key].series.forEach((point) => {
                const row = map.get(point.x) || { date: point.x };
                row[key] = point.y;
                map.set(point.x, row);
            });
        });
        return Array.from(map.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    };

    const trendData = data ? buildTrendFromStats(data) : [];

    const costCount = data?.cost?.count ?? 0;
    const revenueCount = data?.revenue?.count ?? 0;
    const deviceSummary = useMemo(() => {
        const rows = Array.isArray(devicesData) ? devicesData : [];
        return rows
            .map((row) => {
                const deviceKey = normalizeDeviceKey(row?.DEVICE || row?.device);
                const label = deviceLabelMap[deviceKey] || row?.DEVICE || row?.device || 'Unknown';
                const impressions = Number(row?.IMPRESSIONS ?? row?.impressions ?? 0);
                const clicks = Number(row?.CLICKS ?? row?.clicks ?? 0);
                const conversions = Number(row?.CONVERSIONS ?? row?.conversions ?? 0);
                const cost = Number(row?.COST ?? row?.cost ?? 0);
                const revenue = Number(row?.REVENUE ?? row?.revenue ?? 0);
                const ctr = Number(row?.CTR ?? row?.ctr ?? (impressions > 0 ? (clicks / impressions) * 100 : 0));
                const convRate = Number(row?.CONV_RATE ?? row?.CONVRATE ?? row?.convrate ?? (clicks > 0 ? (conversions / clicks) * 100 : 0));
                const roas = cost > 0 ? revenue / cost : 0;
                return {
                    deviceKey,
                    label,
                    impressions,
                    clicks,
                    conversions,
                    cost,
                    revenue,
                    ctr,
                    convRate,
                    roas
                };
            })
            .sort((a, b) => (b.impressions || 0) - (a.impressions || 0));
    }, [devicesData]);

    const deviceTotals = useMemo(() => {
        return deviceSummary.reduce(
            (acc, row) => {
                acc.impressions += row.impressions || 0;
                acc.clicks += row.clicks || 0;
                acc.conversions += row.conversions || 0;
                acc.cost += row.cost || 0;
                acc.revenue += row.revenue || 0;
                return acc;
            },
            { impressions: 0, clicks: 0, conversions: 0, cost: 0, revenue: 0 }
        );
    }, [deviceSummary]);

    const deviceMixBasis = deviceTotals.impressions > 0 ? 'impressions' : 'clicks';
    const deviceLabels = deviceSummary.map((row) => row.label);
    const deviceMixSeries = deviceSummary.map((row) => row[deviceMixBasis] || 0);
    const deviceCostRevenueSeries = [
        { name: 'Spend', data: deviceSummary.map((row) => row.cost || 0) },
        { name: 'Revenue', data: deviceSummary.map((row) => row.revenue || 0) }
    ];
    const deviceRateSeries = [
        { name: 'CTR', data: deviceSummary.map((row) => row.ctr || 0) },
        { name: 'Conv Rate', data: deviceSummary.map((row) => row.convRate || 0) }
    ];
    const deviceRoasSeries = [
        { name: 'ROAS', data: deviceSummary.map((row) => row.roas || 0) }
    ];

    const deviceMixOptions = useMemo(() => ({
        labels: deviceLabels,
        chart: { type: 'donut', zoom: { enabled: false }, toolbar: { show: false } },
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        colors: reportColors.categories,
        plotOptions: {
            pie: {
                donut: {
                    size: '60%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: deviceMixBasis === 'impressions' ? 'Impressions' : 'Clicks',
                            formatter: () => formatNumber(deviceTotals[deviceMixBasis])
                        }
                    }
                }
            }
        },
        tooltip: {
            y: { formatter: (val) => formatNumber(val) }
        }
    }), [deviceLabels, deviceMixBasis, deviceTotals]);

    const deviceCostRevenueOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '50%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        legend: { position: 'top' },
        colors: [reportColors.categories[2], reportColors.revenue],
        xaxis: { categories: deviceLabels, labels: { rotate: -15 } },
        yaxis: {
            labels: { formatter: (val) => formatCurrency(val) }
        },
        tooltip: {
            y: { formatter: (val) => formatCurrency(val) }
        }
    }), [deviceLabels]);

    const deviceRateOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        legend: { position: 'top' },
        colors: [analyticsColors.acquisition, analyticsColors.engagement],
        xaxis: {
            categories: deviceLabels,
            labels: { formatter: (val) => `${val}%` }
        },
        tooltip: {
            y: { formatter: (val) => formatPercent(val) }
        }
    }), [deviceLabels]);

    const deviceRoasOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.revenue],
        xaxis: { categories: deviceLabels, labels: { rotate: -15 } },
        yaxis: {
            labels: { formatter: (val) => `${Number(val).toFixed(2)}x` }
        },
        tooltip: {
            y: { formatter: (val) => `${Number(val).toFixed(2)}x` }
        }
    }), [deviceLabels]);

    const agePieSorted = useMemo(() => {
        const rows = Array.isArray(agePie) ? agePie : [];
        return rows.slice().sort((a, b) => ageSortValue(a?.name) - ageSortValue(b?.name));
    }, [agePie]);

    const genderSorted = useMemo(() => {
        const rows = Array.isArray(genderData) ? genderData : [];
        const order = ['female', 'male', 'unknown'];
        return rows.slice().sort((a, b) => order.indexOf(String(a?.name).toLowerCase()) - order.indexOf(String(b?.name).toLowerCase()));
    }, [genderData]);

    const demographicsTotals = useMemo(() => {
        const ageTotal = agePieSorted.reduce((acc, row) => acc + Number(row?.count || 0), 0);
        const genderTotal = genderSorted.reduce((acc, row) => acc + Number(row?.count || 0), 0);
        const totalClicks = ageTotal || genderTotal || 0;
        const topAge = agePieSorted.reduce(
            (acc, row) => (row?.count > (acc?.count || 0) ? row : acc),
            { name: '—', count: 0 }
        );
        const topGender = genderSorted.reduce(
            (acc, row) => (row?.count > (acc?.count || 0) ? row : acc),
            { name: '—', count: 0 }
        );
        return { totalClicks, topAge, topGender };
    }, [agePieSorted, genderSorted]);

    const ageBarOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [analyticsColors.acquisition],
        xaxis: {
            categories: agePieSorted.map((row) => row?.name || 'Unknown'),
            labels: { formatter: (val) => formatNumber(val) }
        },
        tooltip: {
            y: { formatter: (val) => formatNumber(val) }
        }
    }), [agePieSorted]);

    const ageBarSeries = [{ name: 'Clicks', data: agePieSorted.map((row) => Number(row?.count || 0)) }];

    const genderLabels = genderSorted.map((row) => row?.name || 'Unknown');
    const genderSeries = genderSorted.map((row) => Number(row?.count || 0));
    const genderColors = useMemo(() => {
        const fallback = theme.palette.text?.secondary || theme.palette.grey?.[600] || '#666';
        return genderLabels.map((label) => {
            const key = String(label || '').toLowerCase();
            if (key.includes('female')) return analyticsColors.engagement;
            if (key.includes('male')) return analyticsColors.acquisition;
            return fallback;
        });
    }, [genderLabels, theme]);

    const genderOptions = useMemo(() => ({
        labels: genderLabels,
        chart: { type: 'donut', zoom: { enabled: false }, toolbar: { show: false } },
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        colors: genderColors,
        plotOptions: {
            pie: {
                donut: {
                    size: '60%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Clicks',
                            formatter: () => formatNumber(demographicsTotals.totalClicks)
                        }
                    }
                }
            }
        },
        tooltip: {
            y: { formatter: (val) => formatNumber(val) }
        }
    }), [genderLabels, genderColors, demographicsTotals.totalClicks]);

    const ageGenderRows = useMemo(() => {
        const rows = Array.isArray(ageData) ? ageData : [];
        return rows.slice().sort((a, b) => ageSortValue(a?.ageRange) - ageSortValue(b?.ageRange));
    }, [ageData]);

    const ageGenderCategories = ageGenderRows.map((row) => row?.ageRange || 'Unknown');
    const ageGenderSeries = [
        { name: 'Female', data: ageGenderRows.map((row) => Number(row?.female || 0)) },
        { name: 'Male', data: ageGenderRows.map((row) => Number(row?.male || 0)) },
        { name: 'Unknown', data: ageGenderRows.map((row) => Number(row?.unknown || 0)) }
    ];

    const ageGenderOptions = useMemo(() => ({
        chart: { type: 'bar', stacked: true, zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [
            analyticsColors.engagement,
            analyticsColors.acquisition,
            theme.palette.text?.secondary || theme.palette.grey?.[600] || '#666'
        ],
        xaxis: {
            categories: ageGenderCategories,
            labels: { formatter: (val) => formatNumber(val) }
        },
        legend: { position: 'top' },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    }), [ageGenderCategories, theme]);

    const audienceRows = useMemo(() => {
        const rows = Array.isArray(adsetData) ? adsetData : (Array.isArray(adsetData?.rows) ? adsetData.rows : []);
        return rows.map(row => ({ ...row, ID: row.ADSET_ID || row.ID, CREATIVE: row.ADSET || row.ADSET_NAME || row.NAME || 'Unnamed ad set' }));
    }, [adsetData]);

    const audienceSummary = useMemo(() => {
        return audienceRows.map((row) => {
            const label = row.CREATIVE || row.AUDIENCE || row.CAMPAIGN || row.NAME || 'Unknown';
            const impressions = Number(row.IMPRESSIONS ?? row.impressions ?? 0);
            const clicks = Number(row.CLICKS ?? row.clicks ?? 0);
            const conversions = Number(row.CONVERSIONS ?? row.conversions ?? 0);
            const cost = Number(row.COST ?? row.cost ?? 0);
            const revenue = Number(row.REVENUE ?? row.revenue ?? 0);
            const ctr = Number(row.CTR ?? row.ctr ?? (impressions > 0 ? (clicks / impressions) * 100 : 0));
            const convRate = Number(row.CONV_RATE ?? row.CONVRATE ?? row.convrate ?? (clicks > 0 ? (conversions / clicks) * 100 : 0));
            const roas = cost > 0 ? revenue / cost : 0;
            return {
                label,
                impressions,
                clicks,
                conversions,
                cost,
                revenue,
                ctr,
                convRate,
                roas,
                raw: row
            };
        });
    }, [audienceRows]);

    const locationSummary = useMemo(() => {
        const rows = Array.isArray(locationData) ? locationData : [];
        return rows.map((row) => {
            const label = row.LOCATION || row.NAME || row.COUNTRY || row.REGION || 'Unknown';
            const impressions = Number(row.IMPRESSIONS ?? row.impressions ?? 0);
            const clicks = Number(row.CLICKS ?? row.clicks ?? 0);
            const conversions = Number(row.CONVERSIONS ?? row.conversions ?? 0);
            const cost = Number(row.COST ?? row.cost ?? 0);
            const revenue = Number(row.REVENUE ?? row.revenue ?? 0);
            const ctr = Number(row.CTR ?? row.ctr ?? (impressions > 0 ? (clicks / impressions) * 100 : 0));
            const convRate = Number(row.CONV_RATE ?? row.CONVRATE ?? row.convrate ?? (clicks > 0 ? (conversions / clicks) * 100 : 0));
            const roas = cost > 0 ? revenue / cost : 0;
            return {
                label,
                level: String(row.LEVEL || row.level || 'country'),
                country: String(row.COUNTRY || row.country || ''),
                countryCode: String(row.COUNTRY_CODE || row.countryCode || ''),
                region: String(row.REGION || row.region || (['region', 'state'].includes(String(row.LEVEL || row.level)) ? label : '')),
                impressions,
                clicks,
                conversions,
                cost,
                revenue,
                ctr,
                convRate,
                roas,
                raw: row
            };
        });
    }, [locationData]);

    const topLocationByClicks = useMemo(() => (
        locationSummary.filter(row => row.clicks > 0).sort((a, b) => b.clicks - a.clicks)[0] || null
    ), [locationSummary]);

    const topLocationByImpressions = useMemo(() => (
        locationSummary
            .slice()
            .filter((row) => row.impressions > 0)
            .sort((a, b) => b.impressions - a.impressions)[0] || null
    ), [locationSummary]);

    const locationConversionLabel = useMemo(() => (
        getConversionSelectionLabel(conversionActions.length ? conversionActions : defaultConversionSpec, 'Selected conversion set')
    ), [conversionActions, defaultConversionSpec]);

    const audienceTotals = useMemo(() => {
        return audienceSummary.reduce(
            (acc, row) => {
                acc.impressions += row.impressions;
                acc.clicks += row.clicks;
                acc.conversions += row.conversions;
                acc.cost += row.cost;
                acc.revenue += row.revenue;
                return acc;
            },
            { impressions: 0, clicks: 0, conversions: 0, cost: 0, revenue: 0 }
        );
    }, [audienceSummary]);

    const audienceTopClicks = useMemo(() => {
        return audienceSummary
            .slice()
            .sort((a, b) => b.clicks - a.clicks)
            .slice(0, 8);
    }, [audienceSummary]);

    const audienceTopRoas = useMemo(() => {
        if (!showCost) return [];
        return audienceSummary
            .slice()
            .filter((row) => row.impressions > 0)
            .sort((a, b) => b.impressions - a.impressions)
            .slice(0, 8);
    }, [audienceSummary, showCost]);

    const audienceClickOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [analyticsColors.acquisition],
        xaxis: { categories: audienceTopClicks.map((row) => row.label), labels: { rotate: -15 } },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    }), [audienceTopClicks]);

    const audienceClickSeries = [{ name: 'Clicks', data: audienceTopClicks.map((row) => row.clicks) }];

    const audienceRoasOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.revenue],
        xaxis: { categories: audienceTopRoas.map((row) => row.label), labels: { formatter: (val) => `${Number(val).toFixed(2)}x` } },
        tooltip: { y: { formatter: (val) => `${Number(val).toFixed(2)}x` } }
    }), [audienceTopRoas]);

    const audienceRoasSeries = [{ name: 'ROAS', data: audienceTopRoas.map((row) => row.roas) }];

    const overallAudienceRoas = showCost && audienceTotals.cost > 0 ? audienceTotals.revenue / audienceTotals.cost : null;

    const campaignSummary = useMemo(() => {
        const rows = Array.isArray(campaignData) ? campaignData : [];
        return rows.map((row) => {
            const label = row.CAMPAIGN || row.CAMPAIGN_NAME || row.NAME || 'Unknown';
            const impressions = Number(row.IMPRESSIONS ?? row.impressions ?? 0);
            const clicks = Number(row.CLICKS ?? row.clicks ?? 0);
            const conversions = Number(row.CONVERSIONS ?? row.conversions ?? 0);
            const cost = Number(row.COST ?? row.cost ?? 0);
            const revenue = Number(row.REVENUE ?? row.revenue ?? 0);
            const ctr = Number(row.CTR ?? row.ctr ?? (impressions > 0 ? (clicks / impressions) * 100 : 0));
            const convRate = Number(row.CONV_RATE ?? row.CONVRATE ?? row.convrate ?? (clicks > 0 ? (conversions / clicks) * 100 : 0));
            const roas = cost > 0 ? revenue / cost : 0;
            return {
                label,
                impressions,
                clicks,
                conversions,
                cost,
                revenue,
                ctr,
                convRate,
                roas,
                raw: row
            };
        });
    }, [campaignData]);

    const campaignTopSpend = useMemo(() => {
        if (!showCost) return [];
        return campaignSummary.slice().sort((a, b) => b.cost - a.cost).slice(0, 8);
    }, [campaignSummary, showCost]);

    const campaignTopRoas = useMemo(() => {
        if (!showCost) return [];
        return campaignSummary.slice().filter((row) => row.cost > 0).sort((a, b) => b.roas - a.roas).slice(0, 8);
    }, [campaignSummary, showCost]);

    const campaignTopConversions = useMemo(() => {
        return campaignSummary.slice().sort((a, b) => b.conversions - a.conversions).slice(0, 8);
    }, [campaignSummary]);

    const campaignUnderperformers = useMemo(() => {
        if (!showCost) return [];
        const spenders = campaignSummary.filter((row) => row.cost > 0);
        if (!spenders.length) return [];
        const sortedBySpend = spenders.slice().sort((a, b) => b.cost - a.cost);
        const thresholdIndex = Math.max(0, Math.floor(sortedBySpend.length * 0.5) - 1);
        const costThreshold = sortedBySpend[thresholdIndex]?.cost ?? 0;
        return spenders
            .filter((row) => row.cost >= costThreshold)
            .sort((a, b) => a.roas - b.roas)
            .slice(0, 3);
    }, [campaignSummary, showCost]);

    const campaignSpendOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.categories[2], reportColors.revenue],
        xaxis: { categories: campaignTopSpend.map((row) => row.label), labels: { rotate: -15 } },
        tooltip: { y: { formatter: (val) => formatCurrency(val) } }
    }), [campaignTopSpend]);

    const campaignSpendSeries = [
        { name: 'Spend', data: campaignTopSpend.map((row) => row.cost) },
        { name: 'Revenue', data: campaignTopSpend.map((row) => row.revenue) }
    ];

    const campaignRoasOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [analyticsColors.acquisition],
        xaxis: { categories: campaignTopRoas.map((row) => row.label), labels: { formatter: (val) => `${Number(val).toFixed(2)}x` } },
        tooltip: { y: { formatter: (val) => `${Number(val).toFixed(2)}x` } }
    }), [campaignTopRoas]);

    const campaignRoasSeries = [{ name: 'ROAS', data: campaignTopRoas.map((row) => row.roas) }];

    const campaignConversionOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [analyticsColors.outcome],
        xaxis: { categories: campaignTopConversions.map((row) => row.label), labels: { formatter: (val) => formatNumber(val) } },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    }), [campaignTopConversions]);

    const campaignConversionSeries = [{ name: 'Conversions', data: campaignTopConversions.map((row) => row.conversions) }];

    const engagementRows = useMemo(() => (
        Array.isArray(engagementData?.rows) ? engagementData.rows : []
    ), [engagementData]);

    const engagementTotals = useMemo(() => (
        engagementData?.totals || {
            impressions: 0,
            clicks: 0,
            engagementTotal: 0,
            reactions: 0,
            comments: 0,
            shares: 0,
            saves: 0
        }
    ), [engagementData]);

    const roi = costCount > 0 ? (revenueCount / costCount) * 100 : 0;
    const campaignScopeLabel = Array.isArray(selectedCampaignIds) ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pb: 1 }}>
            Report scope: {selectedCampaignIds.length} of {campaignOptions.length} campaigns selected
        </Typography>
    ) : null;







    return (
        <ThemeProvider theme={theme}><Box sx={pageShellSx}>
            <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
                <Box sx={tabsShellSx}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1,
                            width: '100%'
                        }}
                    >
                        <ResponsiveTabs
                            value={value}
                            onChange={handleChange}
                            indicatorColor="primary"
                            aria-label="Meta report pages"
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ ...tabsSx, flex: '1 1 320px' }}
                        >
                            <Tab label="Performance Overview" {...a11yProps(0)} />
                            <Tab label="Campaign" {...a11yProps(1)} />
                            <Tab label="Audiences" {...a11yProps(2)} />
                            <Tab label="Engagement" {...a11yProps(3)} />
                            <Tab label="Demographics" {...a11yProps(4)} />
                            <Tab label="Device Info" {...a11yProps(5)} />
                            <Tab label="Locations" {...a11yProps(6)} />
                            <Tab label="Notes" {...a11yProps(7)} />
                        </ResponsiveTabs>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, ml: 'auto', flex: '0 0 auto' }}>
                            <ReportToolbarButton
                                onClick={() => {
                                    setDraftCampaignIds(
                                        Array.isArray(selectedCampaignIds)
                                            ? selectedCampaignIds
                                            : campaignOptions.map((option) => option.id)
                                    );
                                    setFiltersOpen(true);
                                }}
                                startIcon={<MdFilterAlt />}
                            >
                                Filter
                            </ReportToolbarButton>
                        </Box>
                    </Box>
                </Box>

                {Array.isArray(selectedCampaignIds) ? (
                    <Alert severity={selectedCampaignIds.length ? 'info' : 'warning'}>
                        {selectedCampaignIds.length
                            ? `${selectedCampaignIds.length} of ${campaignOptions.length} campaigns are included in every report section.`
                            : 'No campaigns are selected. Open Filter to choose campaigns.'}
                    </Alert>
                ) : null}

            <Dialog
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                fullScreen={isMobile}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: surfaceCardSx }}
            >
                <DialogTitle
                    sx={{
                        mb: 2,
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid', borderColor: 'divider'
                    }}
                >
                    Report Filters
                </DialogTitle>
                <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
                    <CampaignSelectionFilter
                        options={campaignOptions}
                        selectedIds={draftCampaignIds}
                        onChange={setDraftCampaignIds}
                        loading={loading}
                    />
                    <ReportConversionSelect
                        service="meta"
                        value={conversionActions}
                        onChange={(next) => {
                            const normalized = normalizeConversionSpec(next);
                            setConversionActions(normalized);
                            sessionStorage.setItem(conversionSessionKey, JSON.stringify(normalized));
                            conversionSessionRef.current = { has: true, value: normalized };
                            fetchReportData(normalized);
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFiltersOpen(false)} sx={filterButtonSx}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            const allSelected = campaignOptions.length > 0 && draftCampaignIds.length === campaignOptions.length;
                            setSelectedCampaignIds(allSelected ? null : [...draftCampaignIds]);
                            setFiltersOpen(false);
                        }}
                    >
                        Apply filters
                    </Button>
                </DialogActions>
            </Dialog>

            {value > 0 ? <ReportSectionIntro report="Meta Ads" title={`Meta · ${META_ASSISTANT_VIEWS[value]}`} dateLabel={pDate} description={[
                '', 'Compare campaign traffic, conversions, and attributed value.', 'See which audiences respond and inspect their creative messages.',
                'Explore reactions, comments, shares, and saved content.', 'Compare the age and gender groups generating response.',
                'Understand delivery, traffic, and outcomes across devices.', 'Explore the locations contributing traffic and attributed outcomes.',
                'Review context and annotations for this reporting period.'
            ][value]} /> : null}
            <Box className="meta-executive-content" sx={{ minWidth: 0 }}>
                <TabPanel value={value} index={0} data-title="Performance Overview">
                    <ReportPerformanceOverview eyebrow="Meta Ads" title="From attention to action"
                        description={`Understand how paid social visibility becomes response and attributed outcomes during ${pDate}.`}
                        badge={`${campaignSummary.length} campaigns`}
                        stages={[
                            { label: 'Acquisition', description: 'Paid social visibility and traffic generated', metrics: [
                                { label: 'Impressions', value: formatNumber(data?.impressions?.count ?? data?.impressions), detail: 'Delivered impressions' },
                                { label: 'Clicks', value: formatNumber(data?.clicks?.count ?? data?.clicks), detail: 'Traffic generated' }
                            ] },
                            { label: 'Response', description: 'How effectively the audience responds', metrics: [
                                { label: 'CTR', value: formatPercent(data?.ctr?.count ?? data?.ctr), detail: 'Click-through rate' },
                                { label: 'Conversion rate', value: formatPercent(data?.convrate?.count ?? data?.convrate), detail: 'Conversions relative to clicks' }
                            ] },
                            { label: 'Outcome', description: 'Attributed actions and their value', metrics: [
                                { label: 'Conversions', value: formatNumber(data?.conversions?.count ?? data?.conversions), detail: 'Recorded conversions' },
                                { label: 'Revenue', value: formatCurrency(data?.revenue?.count ?? data?.revenue), detail: 'Attributed revenue' }
                            ] }
                        ]}
                        supportMetrics={[{ label: 'Average transaction', value: formatCurrency(data?.avgGift?.count ?? data?.avgGift) }, { label: 'Audiences', value: formatNumber(audienceSummary.length) }, ...(showCost ? [{ label: 'CPC', value: formatCurrency(data?.cpc?.count ?? data?.cpc) }, { label: 'Spend', value: formatCurrency(costCount) }] : [])]}
                        insight={campaignTopConversions[0] ? `${campaignTopConversions[0].label} leads campaign conversions with ${formatNumber(campaignTopConversions[0].conversions)} recorded actions.` : 'No campaign conversions were returned for this range.'}
                        highlights={[
                            { label: 'Leading campaign by conversions', title: campaignTopConversions[0]?.label || 'No campaign data', value: formatNumber(campaignTopConversions[0]?.conversions), detail: 'Recorded actions from the leading campaign.' },
                            { label: 'Top audience by clicks', title: audienceTopClicks[0]?.label || 'No audience data', value: formatNumber(audienceTopClicks[0]?.clicks), detail: 'Traffic from the leading audience.' },
                            { label: 'Engagement signal', title: 'Social interactions', value: formatNumber(engagementTotals.engagementTotal), detail: 'Reactions, comments, shares, and saves reported by Meta.' }
                        ]} loading={loading && !data}
                    />
                    <div ref={reportRef1}>
                        {campaignScopeLabel}
                        <Box sx={{ mt: 0 }}>
                            {trendData && Array.isArray(trendData) && trendData.length > 0 ? (
                                <Box sx={{ ...surfaceCardSx, p: { xs: 1, sm: 1.25 } }}>
                                    <TrendChart
                                        reportStyle
                                        trendData={trendData}
                                        showCost={showCost}
                                        showRevenue
                                        theme={theme}
                                        noteType="meta"
                                        noteView="a7edd5c0-7a77-49ed-86ad-6fe3066a0f59"
                                    />
                                </Box>
                            ) : <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ ...surfaceCardSx, mt: 0 }}></Skeleton>}
                        </Box>
                    </div>
                </TabPanel>
                <TabPanel value={value} index={1} data-title="Campaign">
                    <div>
                        <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
                            {campaignData ? (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <TableCard
                                        branded={false}
                                        instanceKey="campaigns"
                                        active={value === 1}
                                        title="Campaigns"
                                        data={campaignData}
                                        count={(campaignData.length > 10 ? 10 : campaignData.length)}
                                        reportProfile={reportProfile}
                                        themeColor={{
                                            primary: analyticsColors.acquisition,
                                            secondary: analyticsColors.acquisition
                                        }}
                                        sx={{ width: '100%' }}
                                        columns={[
                                            {
                                                Header: ({ column }) => (
                                                    <Tooltip title={
                                                        <div>
                                                            <Typography fontWeight={900}>Campaign</Typography>
                                                            <Typography>{'Campaigns being reported'}</Typography>
                                                        </div>
                                                    }>
                                                        <span>Campaign</span>
                                                    </Tooltip>
                                                ),
                                                header: 'Campaign',
                                                accessorKey: "CAMPAIGN",
                                                grow: 1,
                                                size: 300,
                                                Cell: ({ cell }) => (
                                                    <Typography textAlign="left" fontSize={13} fontWeight={900}>
                                                        {cell.row.original.CAMPAIGN}
                                                    </Typography>
                                                ),
                                                Footer: () => (
                                                    <Typography fontSize={13} fontWeight={900} textAlign="left">
                                                        Totals
                                                    </Typography>
                                                ),
                                            }
                                        ]}
                                    />
                                </Box>
                            ) : (
                                <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ borderRadius: 1 }} />
                            )}

                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Campaign Insights
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {showCost ? 'Top performers and spend drivers across campaigns.' : 'Top performers and conversion leaders across campaigns.'}
                                </Typography>
                            </Box>

                            {campaignSummary.length ? (
                                <>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: showCost ? 'repeat(4, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                                        {showCost ? (
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="caption" color="text.secondary">Highest Spend</Typography>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                        {campaignTopSpend[0]?.label || '—'}
                                                    </Typography>
                                                    <Typography variant="body2">{formatCurrency(campaignTopSpend[0]?.cost || 0)}</Typography>
                                                </CardContent>
                                            </Card>
                                        ) : null}
                                        {showCost ? (
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="caption" color="text.secondary">Best ROAS</Typography>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                        {campaignTopRoas[0]?.label || '—'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {campaignTopRoas[0] ? `${campaignTopRoas[0].roas.toFixed(2)}x` : '—'}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        ) : null}
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Most Conversions</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {campaignTopConversions[0]?.label || '—'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {formatNumber(campaignTopConversions[0]?.conversions || 0)}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                        {showCost ? (
                                            <Card variant="outlined" sx={{ borderLeft: `4px solid ${theme.palette.error.main}` }}>
                                                <CardContent>
                                                    <Typography variant="caption" color="text.secondary">Underperformers</Typography>
                                                    {campaignUnderperformers.length ? (
                                                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                                            {campaignUnderperformers.map((row) => (
                                                                <Typography key={row.label} variant="body2">
                                                                    {row.label} • {row.roas.toFixed(2)}x
                                                                </Typography>
                                                            ))}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2">—</Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        ) : null}
                                    </Box>

                                    {showCost ? (
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' }, gap: 2 }}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                        Top Campaigns by Spend (Spend vs Revenue)
                                                    </Typography>
                                                    {campaignTopSpend.length ? (
                                                        <ReactApexChart options={ensureApexOptions(campaignSpendOptions, 'bar', theme)} series={campaignSpendSeries} type="bar" height={300} />
                                                    ) : (
                                                        <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                                    )}
                                                </CardContent>
                                            </Card>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                        Top ROAS Campaigns
                                                    </Typography>
                                                    {campaignTopRoas.length ? (
                                                        <ReactApexChart options={ensureApexOptions(campaignRoasOptions, 'bar', theme)} series={campaignRoasSeries} type="bar" height={300} />
                                                    ) : (
                                                        <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    ) : null}

                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                Most Converting Campaigns
                                            </Typography>
                                            {campaignTopConversions.length ? (
                                                <ReactApexChart options={ensureApexOptions(campaignConversionOptions, 'bar', theme)} series={campaignConversionSeries} type="bar" height={280} />
                                            ) : (
                                                <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
                                            )}
                                        </CardContent>
                                    </Card>
                                </>
                            ) : (
                                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                            )}
                        </Box>
                    </div>
                </TabPanel>
                <TabPanel value={value} index={2} data-title="Audiences">

                    <div ref={reportRef2}>
                        {campaignScopeLabel}
                        <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
                            {loading ? (
                                <Box sx={{ display: 'grid', gap: 2 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <Skeleton key={`audience-kpi-${index}`} variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
                                        ))}
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' }, gap: 2 }}>
                                        <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                        <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                    </Box>
                                    <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                </Box>
                            ) : audienceSummary.length ? (
                                <>
                                    <Box sx={{ overflowX: 'auto' }}>
                                        <TableCard
                                        branded={false}
                                            instanceKey="audiences"
                                            active={value === 2}
                                            getRowId={(r) => `${r.ACCOUNT_ID || ''}-${r.ADSET_ID || r.ID || r.CREATIVE}`}
                                            title="Audience performance by ad set"
                                            data={audienceRows}
                                            count={(audienceRows.length > 10 ? 10 : audienceRows.length)}
                                            client={props.client}
                                            reportProfile={reportProfile}
                                            columns={[
                                                {
                                                    Header: ({ column }) => (
                                                        <Tooltip title={<div><Typography fontWeight={900}>Audience</Typography><Typography>{'Audiences being reported'}</Typography></div>}>
                                                            <span>Audience / ad set</span>
                                                        </Tooltip>
                                                    ),
                                                    header: 'Audience / ad set',
                                                    accessorKey: "CREATIVE",
                                                    grow: 1,
                                                    size: 300,
                                                    Cell: ({ cell }) => (
                                                        <Typography textAlign={'right'} fontSize={13} fontWeight={900} >
                                                            {cell.row.original.CREATIVE}
                                                        </Typography>
                                                    ),
                                                    Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'right'}>Totals</Typography>,
                                                }
                                            ]}
                                            renderDetailPanel={({ row }) => (
                                                <Box sx={{ p: { xs: 1.5, md: 2 }, minWidth: 0 }}>
                                                    <CreativeDetailTabs
                                                        rowData={row.original}
                                                        conversions={conversionActions}
                                                        appVars={appVars}
                                                        sDate={props.sDate}
                                                        eDate={props.eDate}
                                                        fetchImageUrl={fetchImageUrl}
                                                        imageUrls={imageUrls}
                                                        setImageUrls={setImageUrls}
                                                        reportProfile={reportProfile}
                                                    />
                                                </Box>
                                            )}

                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: showCost ? 'repeat(5, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Total Clicks</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatNumber(audienceTotals.clicks)}</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Conversions</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatNumber(audienceTotals.conversions)}</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Revenue</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency(audienceTotals.revenue)}</Typography>
                                            </CardContent>
                                        </Card>
                                        {showCost ? (
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="caption" color="text.secondary">Spend</Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency(audienceTotals.cost)}</Typography>
                                                </CardContent>
                                            </Card>
                                        ) : null}
                                        {showCost ? (
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="caption" color="text.secondary">ROAS</Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{overallAudienceRoas !== null ? `${overallAudienceRoas.toFixed(2)}x` : '—'}</Typography>
                                                </CardContent>
                                            </Card>
                                        ) : null}
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: showCost ? '1.2fr 1fr' : '1fr' }, gap: 2 }}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                    Top Audiences by Clicks
                                                </Typography>
                                                {audienceTopClicks.length ? (
                                                    <ReactApexChart options={ensureApexOptions(audienceClickOptions, 'bar', theme)} series={audienceClickSeries} type="bar" height={280} />
                                                ) : (
                                                    <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
                                                )}
                                            </CardContent>
                                        </Card>
                                        {showCost ? (
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                        Top ROAS Audiences
                                                    </Typography>
                                                    {audienceTopRoas.length ? (
                                                        <ReactApexChart options={ensureApexOptions(audienceRoasOptions, 'bar', theme)} series={audienceRoasSeries} type="bar" height={280} />
                                                    ) : (
                                                        <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
                                                    )}
                                                </CardContent>
                                            </Card>
                                        ) : null}
                                    </Box>
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No audience data available for this range.
                                </Typography>
                            )}
                        </Box>
                    </div>
                </TabPanel>
                <TabPanel ref={reportRef3} value={value} index={3} data-title="Engagement">
                    <div ref={reportRef3}>
                        {campaignScopeLabel}
                        <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
                            <MetaEngagementSection reportStyle
                                loading={loading}
                                rows={engagementRows}
                                totals={engagementTotals}
                                theme={theme}
                                cardSx={surfaceCardSx}
                                entityLabel="Ad Set"
                                emptyMessage="No engagement metrics were returned for this date range."
                                table={(
                                    <Box sx={{ overflowX: 'auto' }}>
                                        <TableCard
                                        branded={false}
                                            instanceKey="engagement"
                                            active={value === 3}
                                            title="Engagement"
                                            data={engagementRows}
                                            count={(engagementRows.length > 10 ? 10 : engagementRows.length)}
                                            reportProfile={reportProfile}
                                            appendDefaultMetrics={false}
                                            themeColor={{
                                                primary: analyticsColors.acquisition,
                                                secondary: analyticsColors.acquisition
                                            }}
                                            sx={{ width: '100%' }}
                                            columns={getMetaEngagementColumns({ entityLabel: 'Ad Set', totals: engagementTotals })}
                                        />
                                    </Box>
                                )}
                            />
                        </Box>
                    </div>
                </TabPanel>
                <TabPanel ref={reportRef3} value={value} index={4} data-title="Demographics" >

                    {(agePie ?
                        <div ref={reportRef3}>
                            {campaignScopeLabel}
                            <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
                                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } }}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="caption" color="text.secondary">
                                                Total Clicks
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {formatNumber(demographicsTotals.totalClicks)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                From demographic breakdown
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="caption" color="text.secondary">
                                                Top Age Group
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {demographicsTotals.topAge?.name || '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatNumber(demographicsTotals.topAge?.count)} clicks
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="caption" color="text.secondary">
                                                Top Gender
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {demographicsTotals.topGender?.name || '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatNumber(demographicsTotals.topGender?.count)} clicks
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Box>

                                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' } }}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                Clicks by Age
                                            </Typography>
                                            {agePieSorted.length ? (
                                                <ReactApexChart options={ensureApexOptions(ageBarOptions, 'bar', theme)} series={ageBarSeries} type="bar" height={320} />
                                            ) : (
                                                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                            )}
                                        </CardContent>
                                    </Card>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                Clicks by Gender
                                            </Typography>
                                            {genderSeries.length ? (
                                                <ReactApexChart options={ensureApexOptions(genderOptions, 'donut', theme)} series={genderSeries} type="donut" height={320} />
                                            ) : (
                                                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                            )}
                                        </CardContent>
                                    </Card>
                                </Box>

                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            Age x Gender Breakdown
                                        </Typography>
                                        {ageGenderRows.length ? (
                                            <ReactApexChart options={ensureApexOptions(ageGenderOptions, 'bar', theme)} series={ageGenderSeries} type="bar" height={360} />
                                        ) : (
                                            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>
                        </div>
                        : '')}
                </TabPanel>
                <TabPanel value={value} index={5} data-title="Device Info" >
         
                    {(devicesData ?
                        <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
                                <TableCard
                                        branded={false}
                                    ref={reportRef4}
                                    instanceKey="devices"
                                    active={value === 5}
                                    data={devicesData}
                                count={(devicesData.length > 10 ? 10 : devicesData.length)}
                                client={props.client}
                                reportProfile={reportProfile}
                                themeColor={{
                                    primary: analyticsColors.acquisition,
                                    secondary: analyticsColors.acquisition
                                }}
                                columns={[
                                    {
                                        Header: ({ column }) => (
                                            <Tooltip title={<div><Typography fontWeight={900}>Device</Typography><Typography>{'Devices being reported'}</Typography></div>}>
                                                <span>Device</span>
                                            </Tooltip>
                                        ),
                                        header: 'Device',
                                        accessorKey: "DEVICE",
                                        grow: 1,
                                        size: 140,
                                        Cell: ({ cell }) => {
                                            const rawDevice = cell.row.original.DEVICE;
                                            const deviceKey = normalizeDeviceKey(rawDevice);
                                            const deviceLabel = deviceLabelMap[deviceKey] || rawDevice || 'Unknown';
                                            const impressions = Number(cell.row.original.IMPRESSIONS ?? 0);
                                            const clicks = Number(cell.row.original.CLICKS ?? 0);
                                            const shareBase = deviceTotals.impressions > 0 ? deviceTotals.impressions : deviceTotals.clicks;
                                            const shareValue = shareBase > 0 ? ((deviceTotals.impressions > 0 ? impressions : clicks) / shareBase) * 100 : 0;

                                            let icon = <IoDesktop size={20} />;
                                            switch (deviceKey) {
                                                case 'iphone':
                                                    icon = (
                                                        <Box>
                                                            <IoLogoApple size={18} />
                                                            <MdPhoneIphone size={18} />
                                                        </Box>
                                                    );
                                                    break;
                                                case 'android_smartphone':
                                                    icon = (
                                                        <Box>
                                                            <FcAndroidOs size={18} />
                                                            <MdOutlinePhoneAndroid size={18} />
                                                        </Box>
                                                    );
                                                    break;
                                                case 'android_tablet':
                                                    icon = (
                                                        <Box>
                                                            <FcAndroidOs size={18} />
                                                            <MdTabletAndroid size={18} />
                                                        </Box>
                                                    );
                                                    break;
                                                case 'ipad':
                                                    icon = (
                                                        <Box>
                                                            <IoLogoApple size={18} />
                                                            <IoIosTabletLandscape size={18} />
                                                        </Box>
                                                    );
                                                    break;
                                                case 'desktop':
                                                    icon = <IoDesktop size={18} />;
                                                    break;
                                                default:
                                                    icon = <IoDesktop size={18} />;
                                                    break;
                                            }

                                            return (
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: firstCol }}>
                                                    <Box sx={{ minWidth: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {icon}
                                                    </Box>
                                                    <Box>
                                                        <Typography fontSize={13} fontWeight={700}>
                                                            {deviceLabel}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {shareValue.toFixed(1)}% of {deviceTotals.impressions > 0 ? 'impressions' : 'clicks'}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            );
                                        },
                                        Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'right'}>Totals</Typography>,
                                    },
                                    ...(showCost ? [{
                                        Header: ({ column }) => (
                                            <Tooltip title={<div><Typography fontWeight={900}>ROAS:</Typography><Typography>{'Revenue divided by cost'}</Typography></div>}>
                                                <span>ROAS</span>
                                            </Tooltip>
                                        ),
                                        header: 'ROAS',
                                        accessorKey: 'ROAS',
                                        grow: true,
                                        maxSize: 100,
                                        Cell: ({ cell }) => {
                                            const row = cell.row.original || {};
                                            const revenue = Number(row.REVENUE ?? row.revenue ?? 0);
                                            const cost = Number(row.COST ?? row.cost ?? 0);
                                            const roas = cost > 0 ? revenue / cost : 0;
                                            return <Typography fontSize={12} fontWeight={600}>{`${roas.toFixed(2)}x`}</Typography>;
                                        },
                                        Footer: () => {
                                            const roasTotal = deviceTotals.cost > 0 ? deviceTotals.revenue / deviceTotals.cost : 0;
                                            return <Typography fontSize={12} fontWeight={700}>{`${roasTotal.toFixed(2)}x`}</Typography>;
                                        },
                                    }] : [])
                                ]}
                            />

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: showCost ? '1.1fr 1.4fr' : '1fr' }, gap: 2 }}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            Device Mix ({deviceMixBasis === 'impressions' ? 'Impressions' : 'Clicks'})
                                        </Typography>
                                        {deviceSummary.length ? (
                                            <ReactApexChart options={ensureApexOptions(deviceMixOptions, 'donut', theme)} series={deviceMixSeries} type="donut" height={300} />
                                        ) : (
                                            <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                        )}
                                    </CardContent>
                                </Card>
                                {showCost ? (
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                Revenue vs Spend by Device
                                            </Typography>
                                            {deviceSummary.length ? (
                                                <ReactApexChart options={ensureApexOptions(deviceCostRevenueOptions, 'bar', theme)} series={deviceCostRevenueSeries} type="bar" height={300} />
                                            ) : (
                                                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                            )}
                                        </CardContent>
                                    </Card>
                                ) : null}
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: showCost ? '1.2fr 1fr' : '1fr' }, gap: 2 }}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            Efficiency by Device (CTR vs Conversion Rate)
                                        </Typography>
                                        {deviceSummary.length ? (
                                            <ReactApexChart options={ensureApexOptions(deviceRateOptions, 'bar', theme)} series={deviceRateSeries} type="bar" height={320} />
                                        ) : (
                                            <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                        )}
                                    </CardContent>
                                </Card>
                                {showCost ? (
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                ROAS by Device
                                            </Typography>
                                            {deviceSummary.length ? (
                                                <ReactApexChart options={ensureApexOptions(deviceRoasOptions, 'bar', theme)} series={deviceRoasSeries} type="bar" height={320} />
                                            ) : (
                                                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
                                            )}
                                        </CardContent>
                                    </Card>
                                ) : null}
                            </Box>
                        </Box>
                        : <div ><Typography style={{ color: analyticsColors.acquisition }} variant='h6' align='center'>Loading Device Data</Typography><LinearProgress color='secondary' style={{ margin: theme.spacing(3) }}></LinearProgress></div>)}


                </TabPanel>
                <TabPanel value={value} index={6} data-title="Locations">
                    <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                                gap: 2,
                                '& > .MuiCard-root': plainCardSx,
                            }}
                        >
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="caption" color="text.secondary">Geo Coverage</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{formatNumber(locationData.length)}</Typography>
                                    <Typography variant="caption" color="text.secondary">Locations returned by Meta insights.</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{locationConversionLabel}</Typography>
                                </CardContent>
                            </Card>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="caption" color="text.secondary">Top State by Clicks</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{topLocationByClicks?.label || '—'}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {topLocationByClicks
                                            ? `${formatNumber(topLocationByClicks.clicks)} clicks · ${topLocationByClicks.ctr.toFixed(2)}% CTR`
                                            : 'No location data returned for the selected range.'}
                                    </Typography>
                                </CardContent>
                            </Card>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="caption" color="text.secondary">Top State by Impressions</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{topLocationByImpressions?.label || '—'}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {topLocationByImpressions ? `${formatNumber(topLocationByImpressions.impressions)} delivered impressions` : 'No delivered impressions were returned.'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>

                        <Card variant="outlined" sx={plainCardSx}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Interactive Geographic Performance</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Compare reported US state performance. Select a state to inspect its totals; Meta does not provide city-level delivery in this breakdown.</Typography>
                                {locationSummary.length > 0 && !locationSummary.some(row => ['region', 'state'].includes(row.level)) && <Alert severity="info" sx={{ mb: 2 }}>Meta returned a country-level breakdown for this selection. State detail is unavailable for these dates.</Alert>}
                                <LocationMap rows={locationSummary} countryScope="us" loading={loading} />
                            </CardContent>
                        </Card>

                        <TableCard
                                        branded={false}
                            instanceKey="locations"
                            active={value === 6}
                            title="Locations"
                            data={locationSummary}
                            count={(locationSummary.length > 10 ? 10 : locationSummary.length)}
                            client={props.client}
                            reportProfile={reportProfile}
                            themeColor={{
                                primary: analyticsColors.acquisition,
                                secondary: analyticsColors.acquisition
                            }}
                            columns={[
                                {
                                    header: 'Location',
                                    accessorKey: 'label',
                                    grow: 1,
                                    minSize: 200,
                                    Cell: ({ cell }) => (
                                        <Typography textAlign={'left'} fontSize={13} fontWeight={900}>{cell.row.original.label || cell.row.original.LOCATION || cell.row.original.NAME || 'Unknown'}</Typography>
                                    ),
                                    Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'left'}>Totals</Typography>,
                                },
                                {
                                    header: 'Level',
                                    accessorKey: 'level',
                                    minSize: 100,
                                    Cell: ({ cell }) => <Typography fontSize={12}>{cell.row.original.raw?.LEVEL || cell.row.original.LEVEL || 'Location'}</Typography>,
                                },
                                {
                                    header: 'Clicks',
                                    accessorKey: 'clicks',
                                    minSize: 100,
                                    Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatNumber(cell.getValue())}</Typography>,
                                    Footer: () => <Typography fontSize={12} fontWeight={700}>{formatNumber(locationSummary.reduce((sum, row) => sum + row.clicks, 0))}</Typography>,
                                },
                                {
                                    header: 'Conversions',
                                    accessorKey: 'conversions',
                                    minSize: 120,
                                    Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatNumber(cell.getValue())}</Typography>,
                                    Footer: () => <Typography fontSize={12} fontWeight={700}>{formatNumber(locationSummary.reduce((sum, row) => sum + row.conversions, 0))}</Typography>,
                                },
                                {
                                    header: 'Revenue',
                                    accessorKey: 'revenue',
                                    minSize: 130,
                                    Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatCurrency(cell.getValue())}</Typography>,
                                    Footer: () => <Typography fontSize={12} fontWeight={700}>{formatCurrency(locationSummary.reduce((sum, row) => sum + row.revenue, 0))}</Typography>,
                                },
                                {
                                    header: 'ROAS',
                                    accessorKey: 'roas',
                                    minSize: 90,
                                    Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{`${Number(cell.getValue() || 0).toFixed(2)}x`}</Typography>,
                                    Footer: () => {
                                        const costTotal = locationSummary.reduce((sum, row) => sum + row.cost, 0);
                                        const revenueTotal = locationSummary.reduce((sum, row) => sum + row.revenue, 0);
                                        const roasTotal = costTotal > 0 ? revenueTotal / costTotal : 0;
                                        return <Typography fontSize={12} fontWeight={700}>{`${roasTotal.toFixed(2)}x`}</Typography>;
                                    },
                                }
                            ]}
                        />
                    </Box>
                </TabPanel>

                <TabPanel ref={reportRef5} value={value} index={7}>
                    {campaignScopeLabel}
                    <Comments eDate={props.eDate} sDate={props.sDate} color={analyticsColors.acquisition} admintoken={admintoken} view={(admintoken >= 6 ? "5af0a295-8b57-4ccb-8cc8-d54e5bb278e6" : "a7edd5c0-7a77-49ed-86ad-6fe3066a0f59")} type="meta" setLoading={props.setLoading}></Comments>
                </TabPanel>
            </Box>
            </Box>
            <ReportQuestionAssistant
                reportType="meta"
                reportTitle="Meta"
                profile={props.reportProfile}
                contextPayload={metaAssistantConfig}
            />
        </Box></ThemeProvider>
    );
};

Meta.propTypes = {

};

export default Meta;
